import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Kanban, MoreHorizontal, Settings } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { useT } from '../utils/translate';
import { usePermissions } from '../utils/permissions';
import { fetchFunnels } from '../store/funnelSlice';
import { NAV_ITEMS } from '../config/navItems';

const PATH = { dashboard: '/dashboard', contacts: '/contacts', tasks: '/tasks', education: '/education', inbox: '/inbox', calls: '/calls', reviews: '/reviews', analytics: '/analytics/sales' };
const ROOT = { analytics: '/analytics' };      // активность раздела определяется по корню пути

// Нижняя навигация телефона и планшета: «капсула» на стекле. Пять главных пунктов + «Ещё» (всё остальное: звонки, отзывы, аналитика, настройки).
// Раньше «Аналитика» и «Отзывы» с телефона были недоступны, а панель вмещала только 6 пунктов.
export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const unread   = useSelector(s => s.inbox?.totalUnread || 0);
  const t = useT();
  const { canSeeModule } = usePermissions();
  const user = useSelector(s => s.auth.user);
  const eduOn = !!user?.organization?.education?.enabled;
  const isAuthed = useSelector(s => s.auth.isAuthenticated);
  const funnels  = useSelector(s => s.funnels.list);
  const fLoaded  = useSelector(s => s.funnels.loaded);
  const [sheet, setSheet] = useState(null);   // null | 'funnels' | 'more'
  const isAdmin = ['owner', 'admin'].includes(user?.role);

  useEffect(() => {
    if (isAuthed && !fLoaded) dispatch(fetchFunnels());
  }, [isAuthed, fLoaded, dispatch]);
  useEffect(() => { setSheet(null); }, [location.pathname]);

  const mod = Object.fromEntries(NAV_ITEMS.map(i => [i.key, i]));
  const item = (key) => ({ key, to: PATH[key], icon: mod[key].icon, label: t(mod[key].labelKey) });
  const visible = (k) => canSeeModule(k);
  const funnelItem = funnels.length > 0 && visible('funnels') !== false
    ? { key: 'funnels', funnel: true, icon: Kanban, label: t('bottomNav.funnels') } : null;

  // Все доступные пункты в привычном порядке; в панель попадают пять самых нужных (приоритет), остальные уходят в «Ещё».
  // У учебного центра «Обучение» важнее «Контактов».
  const all = [
    visible('dashboard') && item('dashboard'),
    eduOn && visible('education') && item('education'),
    funnelItem,
    visible('contacts') && item('contacts'),
    visible('tasks') && item('tasks'),
    visible('inbox') && item('inbox'),
    visible('calls') && item('calls'),
    visible('reviews') && item('reviews'),
    visible('analytics') && item('analytics'),
  ].filter(Boolean);
  const priority = ['dashboard', 'inbox', 'tasks', 'funnels', eduOn ? 'education' : 'contacts', 'contacts', 'calls', 'reviews', 'analytics'];
  const picked = new Set(priority.filter(k => all.some(i => i.key === k)).slice(0, 5));
  const main = all.filter(i => picked.has(i.key));
  const rest = all.filter(i => !picked.has(i.key));
  const showMore = rest.length > 0 || isAdmin;

  const isActive = (it) => {
    if (it.funnel) return location.pathname.startsWith('/funnel/');
    if (it.key === 'dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(ROOT[it.key] || it.to);
  };
  const moreActive = rest.some(isActive) || location.pathname.startsWith('/settings');

  const openFunnels = () => {
    if (funnels.length === 1) navigate(`/funnel/${funnels[0]._id}`);
    else setSheet('funnels');
  };

  const tabCls = (active) => `relative flex flex-col items-center justify-center flex-1 min-w-0 h-full gap-0.5 rounded-2xl transition-colors duration-150 ${active ? 'text-primary-700' : 'text-ink-tertiary'}`;
  const inner = (it, active) => (
    <>
      <span className={`relative flex items-center justify-center w-10 h-7 rounded-full transition-colors duration-200 ${active ? 'bg-primary-500/15' : ''}`}>
        <it.icon className="w-5 h-5" />
        {it.key === 'inbox' && unread > 0 && (
          <span className="absolute -top-1 right-0 min-w-[16px] h-4 bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center px-1">{unread > 99 ? '99+' : unread}</span>
        )}
      </span>
      <span className="text-xs font-medium leading-none truncate max-w-full">{it.label}</span>
    </>
  );

  return (
    <>
      <nav aria-label={t('bottomNav.label')} className="app-bottom-nav lg:hidden fixed bottom-0 inset-x-0 z-30 px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] pointer-events-none">
        <div className="pointer-events-auto flex items-stretch h-16 px-1 rounded-3xl glass-strong">
          {main.map((it) => {
            const active = isActive(it);
            return it.funnel ? (
              <button key="funnels" type="button" onClick={openFunnels} aria-current={active ? 'page' : undefined} className={tabCls(active)}>{inner(it, active)}</button>
            ) : (
              <NavLink key={it.key} to={it.to} aria-current={active ? 'page' : undefined} className={tabCls(active)}>{inner(it, active)}</NavLink>
            );
          })}
          {showMore && (
            <button type="button" onClick={() => setSheet('more')} aria-haspopup="dialog" className={tabCls(moreActive)}>
              {inner({ icon: MoreHorizontal, label: t('bottomNav.more') }, moreActive)}
            </button>
          )}
        </div>
      </nav>

      {sheet && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end" onClick={() => setSheet(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div onClick={e => e.stopPropagation()} className="relative w-full glass-strong rounded-t-3xl shadow-pop max-h-[70dvh] overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))] animate-sheet-up">
            <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-surface-200" aria-hidden="true" />
            <div className="px-5 pt-3 pb-2"><p className="font-semibold text-ink">{sheet === 'funnels' ? t('bottomNav.funnels') : t('bottomNav.more')}</p></div>
            {sheet === 'funnels' ? (
              <div className="divide-y divide-surface-100">
                {funnels.map(f => (
                  <button key={f._id} type="button" onClick={() => navigate(`/funnel/${f._id}`)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 min-h-[48px] text-left hover:bg-surface-100 transition-colors">
                    <Kanban className="w-4 h-4 text-ink-tertiary shrink-0" />
                    <span className="text-sm font-medium text-ink">{f.name}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2 px-4 pb-2">
                {rest.map((it) => (
                  <NavLink key={it.key} to={it.to} className={`flex flex-col items-center gap-1.5 py-3 rounded-2xl min-h-[72px] ${isActive(it) ? 'bg-primary-500/15 text-primary-700' : 'bg-surface-100 text-ink-secondary'}`}>
                    <it.icon className="w-6 h-6" /><span className="text-xs font-medium text-center px-1">{it.label}</span>
                  </NavLink>
                ))}
                {isAdmin && (
                  <NavLink to="/settings" className="flex flex-col items-center gap-1.5 py-3 rounded-2xl min-h-[72px] bg-surface-100 text-ink-secondary">
                    <Settings className="w-6 h-6" /><span className="text-xs font-medium text-center px-1">{t('topbar.settings')}</span>
                  </NavLink>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
